import { useCallback, useEffect, useState } from 'react';
import { Remote } from '@/api/remote';
import { Constants } from '@/constants/constants';
import { ApiConstant } from '@/constants/api_constants';
import { useNavigate, useSearchParams } from 'react-router';
import { NAV_PATH } from '@/router/constants';
import Swiper from 'swiper';
import { Util } from '@/utils/utils';
import { Navigation, Pagination } from 'swiper/modules';

interface FaqItem {
	faqId: string;
	faqTitl: string;
	faqCtgryId: string;
	faqCtgryNm: string;
	ord: string;
	newYn: string;
	faqType: string;
}

interface FaqCategory {
	faqCtgryId: string;
	faqCtgryNm: string;
	ord: string;
}

interface BestItem {
	faqId: string;
	faqCtgryId: string;
	faqCtgryNm: string;
	ord: string;
	faqTitl: string;
	newYn: string;
	faqType: string;
}

interface FaqListResponse {
	list: FaqItem[];
	bestList: BestItem[];
	faqCtgryList: FaqCategory[];
	totalPage: number;
	currentPage: number;
}

const FAQ_PAGE_SIZE = 10; // 페이지당 FAQ 개수
// 📌 API 함수들 (remote.api 그대로 사용)
const getFaqList = async ({ categoryId, srchTxt, page }: { categoryId?: string; srchTxt?: string; page: number }) => {
	const jsonData = {
		faqCtgryNm: categoryId,
		srchTxt: srchTxt
	};
	const response = await Remote.Api.get(`${ApiConstant.mgmtFaq.list}/${page ?? 1}/${FAQ_PAGE_SIZE}`, jsonData);
	if (response.code !== Constants.apiSuccess) {
		alert(response.message);
		return;
	}

	return response;
};

const useFaq = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const [faqInfo, setFaqInfo] = useState<FaqListResponse | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');

	const fetchFaqList = useCallback(async () => {
		try {
			const res = await getFaqList({
				categoryId: searchParams.get('cate') ?? '',
				srchTxt: searchParams.get('srchTxt') ?? '',
				page: Number(searchParams.get('page') ?? 1)
			});
			setFaqInfo(res.content);
			faqSetBestList([
				...(res.content?.bestList ?? []),
				...(res.content?.bestList ?? []),
				...(res.content?.bestList ?? []),
				...(res.content?.bestList ?? []),
				...(res.content?.bestList ?? []),
				...(res.content?.bestList ?? [])
			]);
		} catch (err) {
			console.error('FAQ 리스트 불러오기 실패', err);
		}
	}, [searchValue, searchParams]);

	const faqKeyUpSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			setSearchParams({ page: '1' });
			fetchFaqList();
		}
	};

	const faqClickSearch = () => {
		setSearchParams(prev => {
			prev.set('srchTxt', searchValue);
			prev.set('page', '1');
			return prev;
		});
		fetchFaqList();
	};

	const changeCate = (id: string) => {
		setSearchValue('');
		setSearchParams(prev => {
			prev.set('srchTxt', '');
			prev.set('cate', id);
			prev.set('page', '1');
			return prev;
		});
		scrollToTop();
	};

	const goToDetail = (faqId: string, faqCtgryId: string) => {
		navigate(
			`${NAV_PATH.CUSTOMER_SUPPORT.FAQ_DETAIL}?faqId=${faqId}&faqCtgryId=${faqCtgryId}&cate=${encodeURI(searchParams.get('cate') || '')}&page=${Util.isNullOrEmpty(searchParams.get('page')) ? 1 : searchParams.get('page')}`
		);
	};

	useEffect(() => {
		fetchFaqList();
	}, [searchParams]);

	const scrollToTop = () => {
		const targetScrollY = faqInfo?.bestList?.length ? 520 : 140; // 탭이 보이는 상단
		window.scrollTo({
			top: targetScrollY,
			behavior: 'smooth'
		});
	};

	const faqSetBestList = (list: any[]): void => {
		const FAQ_BEST_PAGE_SIZE = 5;
		if (list.length === 0) {
			// faqHasBest = false;
			document.querySelector('.faq-best')?.classList.add('hidden');
			document.querySelector('.faq .sub-tab')?.classList.add('none-mt');
			return;
		} else {
			document.querySelector('.faq-best')?.classList.remove('hidden');
			document.querySelector('.faq .sub-tab')?.classList.remove('none-mt');
		}

		const boardPageListCon = document.querySelector('.faq-best-swiper .swiper-wrapper') as HTMLElement;
		const pageListLen = Math.ceil(list.length / FAQ_BEST_PAGE_SIZE);

		let elePageList = '';
		for (let i = 0; i < pageListLen; i++) {
			elePageList += '<ul class="best-list swiper-slide"></ul>';
		}
		boardPageListCon.innerHTML = elePageList;

		const cate = Util.isEmpty(searchParams.get('cate'));
		const page = Util.isNullOrEmpty(searchParams.get('page')) ? 1 : searchParams.get('page');

		const boardListContainers = document.querySelectorAll('.faq-best-swiper .swiper-wrapper .best-list');
		boardListContainers.forEach((boardListCon, i) => {
			const start = i * FAQ_BEST_PAGE_SIZE;
			const end = Math.min(start + FAQ_BEST_PAGE_SIZE, list.length);

			for (let j = start; j < end; j++) {
				const { faqId, faqCtgryId, faqTitl } = list[j];
				const link = `${NAV_PATH.CUSTOMER_SUPPORT.FAQ_DETAIL}?faqId=${faqId}&faqCtgryId=${faqCtgryId}&cate=${cate}&page=${page}`;

				const liElement = document.createElement('li');
				const aElement = document.createElement('a');
				aElement.textContent = faqTitl;
				aElement.setAttribute('href', link);
				aElement.classList.add('title');

				aElement.addEventListener('click', e => {
					e.preventDefault();
					navigate(link);
				});

				liElement.appendChild(aElement);
				boardListCon.appendChild(liElement);
			}
		});

		Swiper.use([Pagination, Navigation]);
		const swiper = new Swiper('.faq-best-swiper', {
			loop: true,
			pagination: {
				el: '.swiper-pagination',
				type: 'fraction'
			},
			navigation: {
				nextEl: '.best-item.swiper-button-next',
				prevEl: '.best-item.swiper-button-prev'
			}
		});
		swiper.update();
	};

	return {
		faqInfo,
		goToDetail,
		changeCate,
		searchValue,
		setSearchValue,
		faqKeyUpSearch,
		faqClickSearch
	};
};

export default useFaq;
